import { Link, router } from '@inertiajs/react';
import { useState } from 'react';
import GmLayout from '@/layouts/GmLayout';

interface EnclaveOption {
    id: number;
    name: string;
    owner_name: string | null;
}

interface ItemRecipeOption {
    id: number;
    name: string;
}

interface AdventureOption {
    id: number;
    name: string;
    is_active: boolean;
}

interface SelectOption {
    value: string;
    label: string;
}

interface TargetForm {
    target_type: 'reputation' | 'material' | 'item' | 'quest_completion';
    required_amount: number;
    material_type: string | null;
    item_recipe_id: number | null;
    adventure_id: number | null;
}

interface RewardForm {
    reward_type: 'item' | 'blueprint' | 'material' | 'xp' | 'reputation';
    item_recipe_id: number | null;
    material_type: string | null;
    enclave_id: number | null;
    quantity_min: number | null;
    quantity_max: number | null;
    reputation_min: number | null;
    reputation_max: number | null;
    chance_percent: number;
}

interface RewardBandForm {
    label: string;
    rank_from: number;
    rank_to: number | null;
    rewards: RewardForm[];
}

interface Props {
    enclaves: EnclaveOption[];
    itemRecipes: ItemRecipeOption[];
    adventures: AdventureOption[];
    targetTypes: SelectOption[];
    rewardTypes: Array<SelectOption & { icon?: string }>;
    statuses: SelectOption[];
    materialTypes: string[];
}

const createDefaultTarget = (): TargetForm => ({
    target_type: 'reputation',
    required_amount: 1000,
    material_type: null,
    item_recipe_id: null,
    adventure_id: null,
});

const createDefaultReward = (): RewardForm => ({
    reward_type: 'material',
    item_recipe_id: null,
    material_type: 'tea',
    enclave_id: null,
    quantity_min: 10,
    quantity_max: 10,
    reputation_min: 50,
    reputation_max: 50,
    chance_percent: 100,
});

const createDefaultBand = (): RewardBandForm => ({
    label: 'Top Contributors',
    rank_from: 1,
    rank_to: 1,
    rewards: [createDefaultReward()],
});

export default function Create({ enclaves, itemRecipes, adventures, statuses, materialTypes }: Props) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [enclaveId, setEnclaveId] = useState<number>(enclaves[0]?.id ?? 0);
    const [status, setStatus] = useState<'draft' | 'active'>('draft');
    const [startsAt, setStartsAt] = useState('');
    const [endsAt, setEndsAt] = useState('');
    const [targets, setTargets] = useState<TargetForm[]>([createDefaultTarget()]);
    const [rewardBands, setRewardBands] = useState<RewardBandForm[]>([createDefaultBand()]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addTarget = () => setTargets((prev) => [...prev, createDefaultTarget()]);
    const removeTarget = (index: number) => setTargets((prev) => prev.filter((_, i) => i !== index));

    const updateTarget = (index: number, update: Partial<TargetForm>) => {
        setTargets((prev) => prev.map((target, i) => (i === index ? { ...target, ...update } : target)));
    };

    const addBand = () => setRewardBands((prev) => [...prev, createDefaultBand()]);
    const removeBand = (index: number) => setRewardBands((prev) => prev.filter((_, i) => i !== index));

    const updateBand = (index: number, update: Partial<RewardBandForm>) => {
        setRewardBands((prev) => prev.map((band, i) => (i === index ? { ...band, ...update } : band)));
    };

    const addReward = (bandIndex: number) => {
        setRewardBands((prev) => prev.map((band, index) => {
            if (index !== bandIndex) return band;
            return { ...band, rewards: [...band.rewards, createDefaultReward()] };
        }));
    };

    const removeReward = (bandIndex: number, rewardIndex: number) => {
        setRewardBands((prev) => prev.map((band, index) => {
            if (index !== bandIndex) return band;
            return { ...band, rewards: band.rewards.filter((_, idx) => idx !== rewardIndex) };
        }));
    };

    const updateReward = (bandIndex: number, rewardIndex: number, update: Partial<RewardForm>) => {
        setRewardBands((prev) => prev.map((band, index) => {
            if (index !== bandIndex) return band;
            return {
                ...band,
                rewards: band.rewards.map((reward, idx) => (idx === rewardIndex ? { ...reward, ...update } : reward)),
            };
        }));
    };

    const submit = () => {
        setIsSubmitting(true);

        router.post('/gm/global-challenges', {
            name,
            description: description || null,
            enclave_id: enclaveId,
            status,
            starts_at: startsAt || null,
            ends_at: endsAt || null,
            targets,
            reward_bands: rewardBands,
        }, {
            onFinish: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title="Create Global Challenge" activePath="/gm/global-challenges">
            <div className="max-w-6xl space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-white">Create Global Challenge</h1>
                    <Link href="/gm/global-challenges" className="text-sm text-gray-300 hover:text-white">
                        ← Back to Challenges
                    </Link>
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-5">
                    <h2 className="text-lg font-semibold text-white">Basics</h2>

                    <div>
                        <label className="block text-sm text-gray-300 mb-1">Name</label>
                        <input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            placeholder="e.g. Rebuild the Verdant Embassies"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-gray-300 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-300 mb-1">Enclave</label>
                            <select
                                value={enclaveId}
                                onChange={(event) => setEnclaveId(Number(event.target.value))}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                {enclaves.map((enclave) => (
                                    <option key={enclave.id} value={enclave.id}>
                                        {enclave.owner_name || enclave.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-300 mb-1">Initial Status</label>
                            <select
                                value={status}
                                onChange={(event) => setStatus(event.target.value as 'draft' | 'active')}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                {statuses.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-300 mb-1">Starts At (optional)</label>
                            <input
                                type="datetime-local"
                                value={startsAt}
                                onChange={(event) => setStartsAt(event.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-300 mb-1">Ends At (optional)</label>
                            <input
                                type="datetime-local"
                                value={endsAt}
                                onChange={(event) => setEndsAt(event.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-white">Challenge Targets</h2>
                        <button
                            type="button"
                            onClick={addTarget}
                            className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded"
                        >
                            + Add Target
                        </button>
                    </div>

                    {targets.map((target, index) => (
                        <div key={`target-${index}`} className="p-4 border border-slate-600 rounded-lg space-y-4">
                            <div className="flex justify-between items-center">
                                <p className="text-sm text-gray-300 font-medium">Target {index + 1}</p>
                                {targets.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeTarget(index)}
                                        className="text-xs text-red-300 hover:text-red-200"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Type</label>
                                    <select
                                        value={target.target_type}
                                        onChange={(event) => updateTarget(index, {
                                            target_type: event.target.value as TargetForm['target_type'],
                                            material_type: null,
                                            item_recipe_id: null,
                                            adventure_id: null,
                                        })}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    >
                                        <option value="reputation">Reputation</option>
                                        <option value="material">Materials</option>
                                        <option value="item">Items</option>
                                        <option value="quest_completion">Quest Completion</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Required Amount</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={target.required_amount}
                                        onChange={(event) => updateTarget(index, { required_amount: Number(event.target.value) })}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    />
                                </div>
                            </div>

                            {target.target_type === 'material' && (
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Material</label>
                                    <select
                                        value={target.material_type ?? ''}
                                        onChange={(event) => updateTarget(index, { material_type: event.target.value })}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    >
                                        {materialTypes.map((material) => (
                                            <option key={material} value={material}>{material}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {target.target_type === 'item' && (
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Item Recipe</label>
                                    <select
                                        value={target.item_recipe_id ?? ''}
                                        onChange={(event) => updateTarget(
                                            index,
                                            { item_recipe_id: event.target.value ? Number(event.target.value) : null },
                                        )}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    >
                                        <option value="">Select item recipe</option>
                                        {itemRecipes.map((itemRecipe) => (
                                            <option key={itemRecipe.id} value={itemRecipe.id}>{itemRecipe.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {target.target_type === 'quest_completion' && (
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Adventure</label>
                                    <select
                                        value={target.adventure_id ?? ''}
                                        onChange={(event) => updateTarget(
                                            index,
                                            { adventure_id: event.target.value ? Number(event.target.value) : null },
                                        )}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    >
                                        <option value="">Select adventure</option>
                                        {adventures.map((adventure) => (
                                            <option key={adventure.id} value={adventure.id}>{adventure.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-white">Reward Bands</h2>
                        <button
                            type="button"
                            onClick={addBand}
                            className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded"
                        >
                            + Add Reward Band
                        </button>
                    </div>

                    {rewardBands.map((band, bandIndex) => (
                        <div key={`band-${bandIndex}`} className="p-4 border border-slate-600 rounded-lg space-y-4">
                            <div className="flex justify-between items-center">
                                <p className="text-sm text-gray-300 font-medium">Band {bandIndex + 1}</p>
                                {rewardBands.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeBand(bandIndex)}
                                        className="text-xs text-red-300 hover:text-red-200"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Label</label>
                                    <input
                                        value={band.label}
                                        onChange={(event) => updateBand(bandIndex, { label: event.target.value })}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                        placeholder="Top 3"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Rank From</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={band.rank_from}
                                        onChange={(event) => updateBand(bandIndex, { rank_from: Number(event.target.value) })}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Rank To (blank = infinity)</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={band.rank_to ?? ''}
                                        onChange={(event) => updateBand(
                                            bandIndex,
                                            { rank_to: event.target.value ? Number(event.target.value) : null },
                                        )}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                        placeholder="e.g. 10"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <p className="text-xs uppercase tracking-wide text-gray-400">Rewards</p>
                                    <button
                                        type="button"
                                        onClick={() => addReward(bandIndex)}
                                        className="text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-gray-200"
                                    >
                                        + Add Reward
                                    </button>
                                </div>

                                {band.rewards.map((reward, rewardIndex) => (
                                    <div key={`reward-${bandIndex}-${rewardIndex}`} className="p-3 border border-slate-700 rounded space-y-3">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs text-gray-300">Reward {rewardIndex + 1}</p>
                                            {band.rewards.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeReward(bandIndex, rewardIndex)}
                                                    className="text-xs text-red-300 hover:text-red-200"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Reward Type</label>
                                                <select
                                                    value={reward.reward_type}
                                                    onChange={(event) => updateReward(
                                                        bandIndex,
                                                        rewardIndex,
                                                        { reward_type: event.target.value as RewardForm['reward_type'] },
                                                    )}
                                                    className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                >
                                                    <option value="item">Item</option>
                                                    <option value="blueprint">Blueprint</option>
                                                    <option value="material">Material</option>
                                                    <option value="xp">XP</option>
                                                    <option value="reputation">Reputation</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Chance %</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={100}
                                                    value={reward.chance_percent}
                                                    onChange={(event) => updateReward(
                                                        bandIndex,
                                                        rewardIndex,
                                                        { chance_percent: Number(event.target.value) },
                                                    )}
                                                    className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                />
                                            </div>
                                        </div>

                                        {(reward.reward_type === 'item' || reward.reward_type === 'blueprint') && (
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Item Recipe</label>
                                                <select
                                                    value={reward.item_recipe_id ?? ''}
                                                    onChange={(event) => updateReward(
                                                        bandIndex,
                                                        rewardIndex,
                                                        { item_recipe_id: event.target.value ? Number(event.target.value) : null },
                                                    )}
                                                    className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                >
                                                    <option value="">Select item recipe</option>
                                                    {itemRecipes.map((itemRecipe) => (
                                                        <option key={itemRecipe.id} value={itemRecipe.id}>{itemRecipe.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {reward.reward_type === 'material' && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Material</label>
                                                    <select
                                                        value={reward.material_type ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { material_type: event.target.value },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    >
                                                        {materialTypes.map((material) => (
                                                            <option key={material} value={material}>{material}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Quantity Min</label>
                                                    <input
                                                        type="number"
                                                        value={reward.quantity_min ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { quantity_min: Number(event.target.value) },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Quantity Max</label>
                                                    <input
                                                        type="number"
                                                        value={reward.quantity_max ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { quantity_max: Number(event.target.value) },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {reward.reward_type === 'xp' && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">XP Min</label>
                                                    <input
                                                        type="number"
                                                        value={reward.quantity_min ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { quantity_min: Number(event.target.value) },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">XP Max</label>
                                                    <input
                                                        type="number"
                                                        value={reward.quantity_max ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { quantity_max: Number(event.target.value) },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {reward.reward_type === 'reputation' && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Enclave (optional)</label>
                                                    <select
                                                        value={reward.enclave_id ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { enclave_id: event.target.value ? Number(event.target.value) : null },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    >
                                                        <option value="">Use challenge enclave</option>
                                                        {enclaves.map((enclave) => (
                                                            <option key={enclave.id} value={enclave.id}>
                                                                {enclave.owner_name || enclave.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Rep Min</label>
                                                    <input
                                                        type="number"
                                                        value={reward.reputation_min ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { reputation_min: Number(event.target.value) },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-400 mb-1">Rep Max</label>
                                                    <input
                                                        type="number"
                                                        value={reward.reputation_max ?? ''}
                                                        onChange={(event) => updateReward(
                                                            bandIndex,
                                                            rewardIndex,
                                                            { reputation_max: Number(event.target.value) },
                                                        )}
                                                        className="w-full px-2 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={submit}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded font-medium"
                    >
                        {isSubmitting ? 'Creating...' : 'Create Global Challenge'}
                    </button>
                </div>
            </div>
        </GmLayout>
    );
}
